import { AnyAction } from 'redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DefnProps } from 'configuration-schemas/confdefnComponentProps';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { formatViewDefnError } from 'configuration-schemas/viewdefns/general';
import service from 'src/ServiceContainer';
import { ViewDefnState } from 'src/dao/tenantConfigClient';
import { AppThunkDispatch } from 'src/store';
import { ViewDataState } from 'src/types/Domain';
import { ComponentErrorType, ErrorBoundaryComponentError } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { StyleCollectionViewDefn, zStyleCollectionViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { cacheCheckFetchPivotData } from 'src/pages/Hindsighting/StyleColorReview/StyleColorReview.slice';
import { ListDataOptions } from 'src/worker/pivotWorker.types';

export interface StyleCollectionSlice {
  viewDefn?: StyleCollectionViewDefn;
  viewDefnState: ViewDefnState;
  collectionDataState: ViewDataState;
  collectionCacheHash: string | null;
  itemsDataState: ViewDataState;
  itemsCacheHash: string | null;
}

const initialState: StyleCollectionSlice = {
  viewDefnState: ViewDefnState.idle,
  viewDefn: undefined,
  collectionDataState: ViewDataState.idle,
  itemsDataState: ViewDataState.idle,
  collectionCacheHash: null,
  itemsCacheHash: null,
};

const styleCollectionSlice = createSlice({
  name: 'StyleCollection',
  initialState,
  reducers: {
    requestStyleCollectionConfig(state) {
      state.viewDefnState = ViewDefnState.loading;
    },
    receiveStyleCollectionConfig(state, action: PayloadAction<StyleCollectionViewDefn>) {
      state.viewDefn = action.payload;
      state.viewDefnState = ViewDefnState.loaded;
    },
    requestCollectionData(state) {
      state.collectionDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveCollectionCacheHash(state, action: PayloadAction<string>) {
      state.collectionCacheHash = action.payload;
    },
    receiveCollectionCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.collectionCacheHash) {
        state.collectionDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveCollectionLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.collectionCacheHash) {
        state.collectionDataState = ViewDataState.liveDataReady;
      }
    },
    requestItemsData(state) {
      state.itemsDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveItemsCacheHash(state, action: PayloadAction<string>) {
      state.itemsCacheHash = action.payload;
    },
    receiveItemsCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.itemsCacheHash) {
        state.itemsDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveItemsLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.itemsCacheHash) {
        state.itemsDataState = ViewDataState.liveDataReady;
      }
    },
    receiveError(_state, _action: PayloadAction<ErrorBoundaryComponentError>) {
      return initialState;
    },
    cleanUp() {
      return initialState;
    },
  },
});

export const {
  requestStyleCollectionConfig,
  receiveStyleCollectionConfig,
  requestCollectionData,
  receiveCollectionCacheHash,
  receiveCollectionCachedData,
  receiveCollectionLiveData,
  requestItemsData,
  receiveItemsCacheHash,
  receiveItemsCachedData,
  receiveItemsLiveData,
  cleanUp,
  receiveError,
} = styleCollectionSlice.actions;

export default styleCollectionSlice.reducer;

export function fetchStyleCollectionConfigs(defns: DefnProps) {
  return async (dispatch: AppThunkDispatch): Promise<AnyAction | void> => {
    try {
      const configResponse = await service.tenantConfigClient.getTenantViewDefns<StyleCollectionViewDefn>({
        defnIds: defns.view,
        validationSchemas: [zStyleCollectionViewDefn],
      });

      dispatch(receiveStyleCollectionConfig(configResponse[0]));
    } catch (error) {
      const err = error as any;
      dispatch(
        receiveError({
          type: ComponentErrorType.config,
          message: formatViewDefnError(error),
          name: ConfDefnComponentType.styleCollection,
          issues: error,
          defnId: err.defnId,
        }),
      );
    }
  };
}

export function fetchStyleCollection(model: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    service.pivotService.listDataCacheCheck(model, options),
    requestCollectionData,
    receiveCollectionCacheHash,
    receiveCollectionCachedData,
    receiveCollectionLiveData,
  );
}

export function fetchStyleCollectionItems(model: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    service.pivotService.listDataCacheCheck(model, options),
    requestItemsData,
    receiveItemsCacheHash,
    receiveItemsCachedData,
    receiveItemsLiveData,
  );
}
